<template>
  <div
    class="bg-gradient-to-br from-white to-gray-50 border border-gray-200 relative rounded-xl sm:rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:border-emerald-300 sm:hover:-translate-y-1 group overflow-hidden"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- Overlay pour utilisateur non authentifié -->
    <div
      v-if="!isAuthenticated"
      style="backdrop-filter: brightness(0.8) blur(2px)"
      class="absolute inset-0 backdrop-blur-[3px] bg-white/40 rounded-xl sm:rounded-2xl flex flex-col justify-center items-center z-10 p-4 sm:p-6"
    >
      <div
        class="bg-gradient-to-br from-amber-400 to-orange-500 rounded-full p-3 sm:p-4 mb-2 sm:mb-3 shadow-lg"
      >
        <i class="fa-solid fa-lock text-white text-lg sm:text-xl"></i>
      </div>

      <div class="text-center mb-3 sm:mb-4">
        <h4 class="text-base sm:text-lg font-bold text-gray-800 mb-1 sm:mb-2 text-primary-800">
          Contenu verrouillé
        </h4>
      </div>

      <router-link
        to="/login"
        class="w-full sm:w-auto bg-gradient-to-r cursor-pointer from-primary-300 to-primary-600 hover:from-primary-300 hover:to-primary-700 text-white font-medium py-2 sm:py-3 px-4 sm:px-8 rounded-lg sm:rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 text-sm sm:text-base"
      >
        <i class="fa-solid fa-user-plus text-sm"></i>
        <span>Créer un compte</span>
      </router-link>
    </div>

    <!-- Overlay pour package verrouillé -->
    <div
      v-else-if="lockForPackage"
      style="backdrop-filter: brightness(0.8) blur(2px)"
      class="absolute inset-0 backdrop-blur-[3px] bg-white/40 rounded-xl sm:rounded-2xl flex flex-col justify-center items-center z-10 p-4 sm:p-6"
    >
      <div
        class="bg-gradient-to-br from-amber-400 to-orange-500 rounded-full p-3 sm:p-4 mb-2 sm:mb-3 shadow-lg"
      >
        <i class="fa-solid fa-lock text-white text-lg sm:text-xl"></i>
      </div>

      <div class="text-center mb-3 sm:mb-4">
        <h4 class="text-base sm:text-lg font-bold text-gray-800 mb-1 sm:mb-2 text-primary-800">
          Contenu verrouillé
        </h4>
      </div>

      <router-link
        :to="'/packages?examtype=' + exercice?.examtype_slug"
        class="w-full sm:w-auto bg-gradient-to-r cursor-pointer from-primary-300 to-primary-600 hover:from-primary-300 hover:to-primary-700 text-white font-medium py-2 sm:py-3 px-4 sm:px-8 rounded-lg sm:rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 text-sm sm:text-base"
      >
        <i class="fa-solid fa-box-archive text-sm"></i>
        <span>Acheter un package</span>
      </router-link>
    </div>

    <!-- Contenu principal -->
    <div class="p-4 sm:p-6">
      <!-- Header avec titre et badge tentatives -->
      <div class="flex items-start justify-between mb-3 sm:mb-4">
        <div class="flex-1 min-w-0 cursor-pointer" @click="$emit('start', exercice)">
          <div class="flex items-center space-x-2 sm:space-x-3 mb-2">
            <h3
              class="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors leading-tight truncate"
            >
              {{ exercice.title }}
            </h3>
          </div>

          <p
            v-if="exercice.instructions"
            class="text-gray-600 text-sm leading-relaxed line-clamp-2"
          >
            {{ exercice.instructions }}
          </p>
        </div>

        <div v-if="isAuthenticated && hasAttempts" class="ml-3 sm:ml-4 flex-shrink-0">
          <div
            class="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
          >
            {{ attemptCount }} tentative{{ attemptCount > 1 ? 's' : '' }}
          </div>
        </div>
      </div>

      <!-- Informations temps et questions - Même ligne -->
      <div class="flex items-center space-x-3 sm:space-x-6 mb-4 sm:mb-6">
        <div class="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg flex-1">
          <div
            class="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-primary-400 to-primary-600 rounded-md flex items-center justify-center flex-shrink-0"
          >
            <i class="fa-solid fa-clock text-white text-xs"></i>
          </div>
          <span class="text-xs sm:text-sm font-medium text-gray-700 truncate">
            {{ exercice.time_limit ? `${exercice.time_limit / 60} min` : 'Pas de limite' }}
          </span>
        </div>

        <div class="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg flex-1">
          <div
            class="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-primary-400 to-primary-600 rounded-md flex items-center justify-center flex-shrink-0"
          >
            <i class="fa-solid fa-list text-white text-xs"></i>
          </div>
          <span class="text-xs sm:text-sm font-medium text-gray-700 truncate">
            {{ exercice?.nb_question }} Questions
          </span>
        </div>
      </div>

      <!-- Bouton démarrer - Optimisé mobile -->
      <div v-if="isAuthenticated && !lockForPackage" class="mb-3 sm:mb-4">
        <button
          class="w-full bg-gradient-to-r cursor-pointer from-primary-400 to-primary-600 hover:from-primary-500 hover:to-primary-700 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 sm:space-x-3 text-sm sm:text-base"
          @click="$emit('start', exercice)"
        >
          <div
            class="w-6 h-6 sm:w-8 sm:h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0"
          >
            <i class="fa-solid fa-play text-xs sm:text-sm"></i>
          </div>
          <span class="font-medium sm:text-lg">Démarrer l'exercice</span>
        </button>
      </div>

      <!-- Section historique - Mobile responsive -->
      <transition
        enter-active-class="transition-all duration-300"
        enter-from-class="opacity-0 transform translate-y-4"
        enter-to-class="opacity-100 transform translate-y-0"
        leave-active-class="transition-all duration-200"
        leave-from-class="opacity-100 transform translate-y-0"
        leave-to-class="opacity-0 transform translate-y-4"
      >
        <div
          v-if="hasAttempts && !lockForPackage"
          class="border-t border-gray-100 pt-3 sm:pt-4 mt-3 sm:mt-4"
        >
          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0"
          >
            <!-- Meilleur score -->
            <div class="flex items-center space-x-2 sm:space-x-3">
              <div
                class="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0"
              >
                <i class="fa-solid fa-trophy text-white text-xs sm:text-sm"></i>
              </div>
              <div>
                <p class="text-xs text-gray-500 font-medium">Meilleur score</p>
                <p class="text-base sm:text-lg font-bold" :class="getScoreClass(bestScore)">
                  {{ bestScore }}
                </p>
              </div>
            </div>

            <!-- Bouton historique -->
            <button
              @click.stop="showHistory"
              class="w-full sm:w-auto flex items-center cursor-pointer justify-center sm:justify-start space-x-2 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 text-sm"
              title="Voir l'historique des tentatives"
            >
              <i class="fa-solid fa-history text-sm"></i>
              <span class="font-medium">Historique</span>
            </button>
          </div>
        </div>
      </transition>

      <!-- Version mobile toujours visible pour l'historique -->
      <div
        v-if="hasAttempts && !isHovered && isMobile && !lockForPackage"
        class="border-t border-gray-100 pt-3 mt-3"
      >
        <button
          @click.stop="showHistory"
          class="w-full flex items-center cursor-pointer justify-center space-x-2 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 text-gray-700 px-3 py-2 rounded-lg transition-all duration-200 text-sm border border-gray-200"
          title="Voir l'historique des tentatives"
        >
          <i class="fa-solid fa-history text-sm"></i>
          <span class="font-medium">Voir l'historique ({{ attemptCount }} tentatives)</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Modal historique -->
  <ExerciceHistoryModal
    v-if="showHistoryModal"
    :exercice="exercice"
    :attempts="attempts"
    @close="closeHistory"
    @view-result="handleViewResult"
    @retry-from="handleRetryFrom"
  />
</template>

<script setup>
import { defineProps, defineEmits, ref, computed, onMounted } from 'vue'
import ExerciceHistoryModal from './ExerciceHistoryModal.vue'
import { useLanguageStore } from '@/stores/language'
import { useAuthStore } from '@/stores/auth.ts'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  exercice: {
    type: Object,
    required: true,
  },
  lockForPackage: {
    default: false,
    required: false,
  },
})

const emit = defineEmits(['start', 'view-result'])

const store = useLanguageStore()
const authStore = useAuthStore()

const { error: showError } = useToast()

// State
const showHistoryModal = ref(false)
const attempts = ref([])
const isLoadingAttempts = ref(false)
const isHovered = ref(false)

// Computed
const hasAttempts = computed(() => attempts.value.length > 0)
const attemptCount = computed(() => attempts.value.length)
const isAuthenticated = computed(() => authStore.isAuthenticated)

const bestScore = computed(() => {
  if (attempts.value.length === 0) return null
  const completedAttempts = attempts.value.filter(
    (attempt) => attempt.completed_at && attempt.score !== null,
  )
  if (completedAttempts.length === 0) return null
  return Math.max(...completedAttempts.map((attempt) => attempt.score))
})

// Methods
const getScoreClass = (score) => {
  if (score >= 80) return 'text-green-600'
  if (score >= 60) return 'text-yellow-600'
  return 'text-red-600'
}

const showHistory = async () => {
  if (attempts.value.length === 0) {
    await loadAttempts()
  }
  showHistoryModal.value = true
}

const closeHistory = () => {
  showHistoryModal.value = false
}

const handleViewResult = (attempt) => {
  emit('view-result', attempt)
  closeHistory()
}

const handleRetryFrom = (attempt) => {
  emit('start', props.exercice)
  closeHistory()
}

const loadAttempts = async () => {
  isLoadingAttempts.value = true

  attempts.value = props.exercice.user_exercice_attempts

  isLoadingAttempts.value = false
}

// Lifecycle
onMounted(() => {
  loadAttempts()
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
